import React, { useEffect, useState, useCallback } from "react";
import { connect } from 'react-redux';
import RoleForm from "./RoleForm";
import { CircularProgress } from "@material-ui/core";
import * as Yup from "yup";
import { parseURL } from "../../sc-utilities";
import * as actions from './store/roleActions';
import History from "../../../History";
import { hasPermission } from "../../sc-utilities";
import { CancelToken } from 'axios';

const RoleEdit = props => {
    const { listPermissions, activeCustomerID, loadRole, translations, role, loadRoleInit, updateRole, permissions: userPermissions, createRole } = props;
    const [editData, setEditData] = useState({ permissions: [], formattedPermissions: [], loadingData: true });

    const searchParams = parseURL(window.location.href);

    useEffect(() => {
        const requestToken = CancelToken.source();
        const fetchData = async () => {
            await loadRole({ ActiveCustomerID: activeCustomerID, RoleID: parseURL(window.location.href).roleID }, { cancelToken: requestToken.token })
        }
        fetchData();
        return () => {
            requestToken.cancel('Operation canceled by the user.');
            loadRoleInit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formatPermissions = useCallback(async (permissions = [], rolePermissions = []) => {
        let rolePermissionIDs = rolePermissions.map(permission => permission.PermissionID);
        return permissions.filter(permission => rolePermissionIDs.includes(permission.value));
    }, [])

    const formatRoleData = useCallback(async (role, requestToken) => {
        let permissions = await listPermissions({ ActiveCustomerID: activeCustomerID }, { cancelToken: requestToken.token });
        let formattedPermissions = await formatPermissions(permissions, role.Permissions);
        setEditData({ permissions: permissions, formattedPermissions: formattedPermissions, loadingData: false });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        const requestToken = CancelToken.source();
        if (role && Object.keys(role).length > 0) {
            formatRoleData(role, requestToken);
        }
        return () => {
            requestToken.cancel('Operation canceled by the user.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [role]);

    const extractPermissions = selectedPermission => {
        return selectedPermission.map(permission => permission.value);
    }

    const handleSubmit = async data => {
        let formData = { ...data };
        formData['CustomerID'] = role.CustomerID;
        formData['Permissions'] = extractPermissions(formData.Permissions);
        delete formData.FormattedPermissions;

        let result;
        if (searchParams?.copy) {
            result = await createRole({ ...formData, ActiveCustomerID: activeCustomerID, RoleID: parseURL(window.location.href).roleID });
        } else {
            result = await updateRole({ ...formData, ActiveCustomerID: activeCustomerID, RoleID: parseURL(window.location.href).roleID });
        }
        if (result) {
            setEditData({ permissions: [], formattedPermissions: [], loadingData: true });
            History.goBack()
        }
    };

    let initialValues = {};

    if (searchParams?.copy) {
        initialValues = {
            Name: role && role.Name ? `${role.Name}-copy` : '',
            FancyName: role && role.FancyName ? `${role.FancyName}-copy` : '',
            Type: role && role.Type ? role.Type : 'public',
            Module: role && role.Module ? `${role.Module}-copy` : '',
            Locked: role && role.Locked ? role.Locked : false,
            Permissions: editData.formattedPermissions,
        };
    } else {
        initialValues = {
            Name: role && role.Name ? role.Name : '',
            FancyName: role && role.FancyName ? role.FancyName : '',
            Type: role && role.Type ? role.Type : 'public',
            Module: role && role.Module ? role.Module : '',
            Locked: role && role.Locked ? role.Locked : false,
            Permissions: editData.formattedPermissions,
        };
    }

    let formSettings = {
        Validation: {
            Name: Yup.string().required("Required"),
            FancyName: Yup.string().required("Required"),
            Type: Yup.string().required("Required"),
            Locked: Yup.boolean().required("Required"),
        },
        FormTitle: searchParams?.copy? translations["create_role"] || "Create role" : translations["edit_role"] || "Edit role",
        ButtonSubmit: hasPermission(userPermissions, 'role_save') ? {
            Title: translations["save"] || "SAVE"
        } : null
    };

    return (
        !editData.loadingData ?
            <RoleForm
                formSettings={formSettings}
                initialValues={initialValues}
                handleSubmit={handleSubmit}
                translations={translations}
                permissions={editData.permissions}
                isEditForm={true}
                customerID={role.CustomerID}
                userPermissions={userPermissions}
            /> :
            <CircularProgress />
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        role: state.role.roles.loaded,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || []
    };
};

const mapDispatchToProps = dispatch => {
    return {
        updateRole: data => dispatch(actions.editRole(data)),
        createRole: data => dispatch(actions.createRole(data)),
        listPermissions: (data, options) => dispatch(actions.loadPermission(data, options)),
        loadRole: (data, options) => dispatch(actions.loadRole(data, options)),
        loadRoleInit: () => dispatch(actions.loadRoleInit()),
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(RoleEdit);
